import React, { useEffect, useState, useRef } from 'react';
const expertiseAreas = [{
  number: '01',
  title: 'AI Strategy',
  description: "I help teams figure out where AI actually earns its place in a product and where it just adds noise. At Eli Lilly I coach design teams on building AI into their workflows, and at MoneyGram I ran a head-to-head evaluation of enterprise AI platforms that unlocked the next round of funding. I've also worked the evaluation side directly, rating and stress-testing model outputs as a contributor at Mercor and Micro1, which sharpened how I judge where models are reliable and where they break.",
  skills: ['MODEL EVALUATION', 'PLATFORM EVALUATION', 'CONVERSATIONAL AI', 'AI WORKFLOWS']
}, {
  number: '02',
  title: 'UX Design',
  description: 'Eight years of taking complex enterprise problems and making them usable. At U.S. Bank I redesigned the One Card payment flow and helped cut handling time from 54 days to 20. At Case Western I ran 16 faculty interviews in a two-week sprint to design an AI CV parser people actually trusted. I lean on contextual inquiry, usability testing, and a lot of iteration.',
  skills: ['USER RESEARCH', 'CONTEXTUAL INQUIRY', 'WIREFRAMING', 'USABILITY TESTING']
}, {
  number: '03',
  title: 'UI Design',
  description: 'I build interfaces that hold up under real enterprise complexity, not just clean mockups. On the Unisys Stealth cybersecurity platform I turned dense micro-segmentation and threat-analysis data into something a security team could read and act on, and consolidated four mismatched component libraries into a single system along the way.',
  skills: ['DESIGN SYSTEMS', 'VISUAL DESIGN', 'INTERACTION DESIGN', 'DATA VISUALIZATION']
}, {
  number: '04',
  title: 'AI Engineering',
  description: "I've been embedded on engineering teams for the last year and a half across three corporate product teams, helping push design work into production. I'm actively studying the code and cloud infrastructure side of product, building my technical foundation so I can speak engineering's language and design more technically feasible solutions.",
  skills: ['CSS', 'HTML', 'JAVASCRIPT', 'PYTHON']
}];
export function ExpertiseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<boolean[]>([false, false, false, false]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stagger the main items
          expertiseAreas.forEach((_, index) => {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 180);
          });
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} id="expertise" className="bg-white py-24 sm:py-32 md:py-40 px-6 sm:px-12 md:px-16 lg:px-24 w-full overflow-hidden" data-id="element-1534">
      <div className="container mx-auto max-w-7xl" data-id="element-1535">
        {/* Unified Section Header */}
        <div className="mb-16 sm:mb-24" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
      }} data-id="element-1536">
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 font-medium mb-4" style={{
          fontFamily: "'Ronzino', sans-serif"
        }} data-id="element-1537">
            EXPERTISE
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-6" style={{
          fontFamily: "'Gefika', sans-serif"
        }} data-id="element-1538">
            Top Skills
          </h2>
          <div className="w-12 h-px bg-slate-900" data-id="element-1539"></div>
        </div>

        <div className="max-w-6xl mx-auto" data-id="element-1540">
          {expertiseAreas.map((area, index) => <div key={index} className="mb-16 sm:mb-20 last:mb-0 pb-16 sm:pb-20 border-b border-slate-200 last:border-b-0 last:pb-0" style={{
          opacity: visibleItems[index] ? 1 : 0,
          transform: visibleItems[index] ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
          willChange: 'transform, opacity'
        }} data-id="element-1541">
              <div className="grid grid-cols-[auto_1fr] gap-5 sm:gap-8 items-baseline" data-id="element-1542">
                {/* Number column - quiet editorial label */}
                <div className="w-12 sm:w-16 text-2xl sm:text-3xl md:text-4xl font-light text-slate-500 tracking-tight tabular-nums leading-tight" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-1543">
                  {area.number}
                </div>

                {/* Content column */}
                <div className="max-w-3xl min-w-0" data-id="element-1544">
                  <div className="flex flex-wrap items-center gap-4" data-id="element-1545">
                    <h3 className="text-slate-900 text-2xl sm:text-3xl md:text-4xl leading-none" style={{
                  fontFamily: "'Gefika', sans-serif",
                  fontWeight: 400
                }} data-id="element-1546">
                      {area.title}
                    </h3>
                    <span className="bg-slate-900 text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1547">
                      {index === 0 ? '3 YRS' : index === 1 ? '8 YRS' : index === 2 ? '3 YRS' : '1.5 YRS'}
                    </span>
                  </div>

                  <div className="mt-6 mb-8 w-full h-px bg-slate-200" data-id="element-1548"></div>

                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontWeight: 400,
                letterSpacing: '0.01em'
              }} data-id="element-1549">
                    {area.description}
                  </p>

                  <div className="flex flex-wrap gap-2" data-id="element-1550">
                    {area.skills.map((skill, skillIndex) => <span key={skillIndex} className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs uppercase tracking-[0.12em] text-slate-700 font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1551">
                        {skill}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}